import { ApiRequestError, toApiError } from './api-error';

/** Header the API stamps every response with, one correlation id per request (R17.8). */
const CORRELATION_HEADER = 'x-correlation-id';

/**
 * The single fetch wrapper every query and mutation goes through.
 *
 * Responsibilities kept here so no call site repeats them:
 * - send and receive JSON, credentials included so the session cookie travels;
 * - on a non-2xx, parse the body into one of the six error shapes and throw an
 *   {@link ApiRequestError}, so callers branch on a typed `code` rather than on a
 *   status number;
 * - surface the correlation id even when the error body is missing or malformed,
 *   so a support request always has one identifier to quote.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const correlationId = response.headers.get(CORRELATION_HEADER) ?? '';
  const payload: unknown = await parseJsonSafely(response);

  if (!response.ok) {
    throw new ApiRequestError(response.status, toApiError(response.status, payload, correlationId));
  }

  return payload as T;
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
